import type { StandProfile } from '@vv/common';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckCircleIcon, FactIcon } from '../icons';
import { RichText } from '../RichText';

import { PieChart } from './PieChart';

/** A named attribute row: benefits, perks and "Zanimljivosti" share this shape. */
function AttributeList({
  items,
  showIcons,
}: {
  items: StandProfile['benefits'];
  showIcons: boolean;
}): ReactElement {
  return (
    <ul className={showIcons ? 'space-y-3' : 'space-y-2'}>
      {items.map((item) => (
        <li
          key={item.id}
          className={`card flex items-center gap-3 ${showIcons ? 'p-3' : 'px-4 py-2.5'}`}
        >
          {showIcons ? (
            <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f4a9bd] text-[#7a1633]">
              {item.iconUrl === null ? (
                <FactIcon className="size-6" />
              ) : (
                <img src={item.iconUrl} alt="" className="size-full object-cover" />
              )}
            </span>
          ) : (
            <CheckCircleIcon className="size-5 shrink-0 text-pav-services" />
          )}
          <span className="text-sm text-fog-300">{item.name}</span>
        </li>
      ))}
    </ul>
  );
}

export function WhyUsTab({ profile }: { profile: StandProfile }): ReactElement {
  const { t } = useTranslation();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeChart, setActiveChart] = useState(0);

  return (
    <div className="space-y-10">
      {/* Exhibitor-authored HTML from the database, not plain text: rendering it
          as a string put the raw <p> tags on screen. RichText sanitises it. */}
      {profile.about !== null && profile.about !== '' && (
        <RichText html={profile.about} className="text-sm leading-relaxed text-fog-300" />
      )}

      {profile.infos.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-bold text-fog-100">{t('stand.facts')}</h2>
          <AttributeList items={profile.infos} showIcons />
        </section>
      )}

      {profile.testimonials.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-bold text-fog-100">{t('stand.testimonials')}</h2>
          <div
            className="carousel"
            onScroll={(event) => {
              const track = event.currentTarget;
              const first = track.firstElementChild;

              if (first !== null) {
                const width = first.getBoundingClientRect().width + 12;
                setActiveTestimonial(Math.round(track.scrollLeft / width));
              }
            }}
          >
            {profile.testimonials.map((testimonial) => (
              <article key={testimonial.id} className="card w-full max-w-full p-4">
                <header className="mb-3 flex items-center gap-3">
                  {testimonial.imageUrl !== null && (
                    <img src={testimonial.imageUrl} alt="" className="size-11 rounded-full" />
                  )}
                  <div>
                    <p className="font-bold text-fog-100">{testimonial.name}</p>
                    {testimonial.role !== null && (
                      <p className="text-sm text-fog-500">{testimonial.role}</p>
                    )}
                  </div>
                </header>
                {testimonial.content !== null && (
                  <blockquote className="text-sm leading-relaxed text-fog-300">
                    {testimonial.content}
                  </blockquote>
                )}
              </article>
            ))}
          </div>
          {profile.testimonials.length > 1 && (
            <div className="mt-3 flex justify-center gap-1.5">
              {profile.testimonials.map((testimonial, index) => (
                <span
                  key={testimonial.id}
                  aria-hidden="true"
                  className={`size-1.5 rounded-full ${
                    index === activeTestimonial ? 'bg-fog-300' : 'bg-ink-600'
                  }`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {profile.benefits.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-bold text-fog-100">{t('stand.benefits')}</h2>
          <AttributeList items={profile.benefits} showIcons={false} />
        </section>
      )}

      {profile.perks.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-bold text-fog-100">{t('stand.perks')}</h2>
          <AttributeList items={profile.perks} showIcons={false} />
        </section>
      )}

      {profile.charts.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-bold text-fog-100">{t('stand.profile')}</h2>
          <div
            className="carousel"
            onScroll={(event) => {
              const track = event.currentTarget;
              const first = track.firstElementChild;

              if (first !== null) {
                const width = first.getBoundingClientRect().width + 12;
                setActiveChart(Math.round(track.scrollLeft / width));
              }
            }}
          >
            {profile.charts.map((chart) => (
              <div key={chart.id} className="w-full max-w-full">
                <PieChart chart={chart} />
              </div>
            ))}
          </div>
          {profile.charts.length > 1 && (
            <div className="mt-3 flex justify-center gap-1.5">
              {profile.charts.map((chart, index) => (
                <span
                  key={chart.id}
                  aria-hidden="true"
                  className={`size-1.5 rounded-full ${
                    index === activeChart ? 'bg-fog-300' : 'bg-ink-600'
                  }`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <h2 className="mb-3 text-xl font-bold text-fog-100">{t('stand.contact')}</h2>
        <div className="space-y-1 text-sm text-fog-300">
          {profile.contact.name !== null && (
            <p className="font-semibold text-fog-100">{profile.contact.name}</p>
          )}
          {profile.contact.email !== null && (
            <p>
              {t('stand.contactEmail')}{' '}
              <a href={`mailto:${profile.contact.email}`} className="text-primary">
                {profile.contact.email}
              </a>
            </p>
          )}
          {profile.contact.phone !== null && (
            <p>
              {t('stand.contactPhone')} {profile.contact.phone}
            </p>
          )}
          {profile.contact.address !== null && (
            <p>
              {t('stand.contactAddress')} {profile.contact.address}
            </p>
          )}
          {profile.contact.website !== null && (
            <p>
              {t('stand.contactWeb')}{' '}
              <a
                href={`https://${profile.contact.website.replace(/^https?:\/\//, '')}`}
                target="_blank"
                rel="noreferrer noopener"
                className="text-primary"
              >
                {profile.contact.website}
              </a>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
